import React from "react"
import { Helmet } from "react-helmet-async"
import { RefreshCw, Calendar, AlertTriangle, CreditCard, Truck, Phone } from "lucide-react"

const RefundCancellationPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Refund and Cancellation Policy - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Our refund and cancellation policy. Cancel within 10 days of order placement. Refunds processed within 4 days of approval." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Refund and Cancellation Policy</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Guidelines for cancelling orders and processing refunds
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Cancellation Policy */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <RefreshCw className="w-6 h-6 mr-3 text-primary-600" />
                Cancellation Policy
              </h2>
              
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-6">
                <div className="flex items-center text-blue-700 mb-2">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span className="font-semibold text-lg">10-Day Cancellation Window</span>
                </div>
                <p className="text-blue-600">
                  Cancellations will only be considered if the request is made within 10 days of placing the order
                </p>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 border-amber-400 pl-4">
                  <h3 className="font-semibold text-gray-900 mb-2">When Cancellation May Not Be Possible:</h3>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Orders have been communicated to sellers/merchants listed on the Platform</li>
                    <li>• Sellers have initiated the shipping process</li>
                    <li>• Product is out for delivery</li>
                  </ul>
                  <p className="text-gray-600 text-sm mt-3">
                    <strong>Alternative:</strong> In such cases, you may choose to reject the product at the doorstep
                  </p>
                </div>
              </div>
            </div>

            {/* Special Item Policies */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-amber-500" />
                Special Item Cancellation Rules
              </h2>
              
              <div className="space-y-6">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-2">Non-Cancellable Items:</h3>
                  <p className="text-red-700">
                    Anmol Furniture Bazar does not accept cancellation requests for perishable items like flowers, eatables, etc.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2">Quality Issues:</h3>
                  <p className="text-green-700">
                    However, refund/replacement can be made if the user establishes that the quality of the product delivered is not good.
                  </p>
                </div>
              </div>
            </div>

            {/* Damage & Defect Reports */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Truck className="w-6 h-6 mr-3 text-primary-600" />
                Damaged or Defective Items
              </h2>
              
              <div className="space-y-4">
                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold text-yellow-900 mb-3">Reporting Timeline: 10 Days</h3>
                  <p className="text-yellow-800 mb-3">
                    In case of receipt of damaged or defective items, please report to our customer service team within 
                    <strong> 10 days of receipt of products</strong>.
                  </p>
                  <p className="text-yellow-700 text-sm">
                    The request will be entertained once the seller/merchant listed on the Platform has checked and 
                    determined the same at its own end.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">Product Not as Expected:</h3>
                  <p className="text-blue-700">
                    If you feel that the product received is not as shown on the site or as per your expectations, 
                    you must bring it to the notice of our customer service within 10 days of receiving the product. 
                    The customer service team will review your complaint and take an appropriate decision.
                  </p>
                </div>
              </div>
            </div>

            {/* Refund Processing */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <CreditCard className="w-6 h-6 mr-3 text-primary-600" />
                Refund Processing
              </h2>
              
              <div className="bg-green-50 p-6 rounded-lg border border-green-200 mb-4">
                <div className="flex items-center text-green-700 mb-2">
                  <CreditCard className="w-5 h-5 mr-2" />
                  <span className="font-semibold text-lg">Refund Timeline: 4 Days</span>
                </div>
                <p className="text-green-600">
                  In case of any refunds approved by Anmol Furniture Bazar, it will take 4 days for the refund to be processed to you
                </p>
              </div>

              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  Refunds will be processed to the original payment method used during purchase. 
                  Please allow additional time for your bank or payment provider to reflect the refund in your account.
                </p>
              </div>
            </div>

            {/* Warranty Items */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-amber-500" />
                Warranty Items
              </h2>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-amber-800">
                  <strong>Manufacturer Warranty:</strong> In case of complaints regarding products that come with 
                  a warranty from the manufacturers, please refer the issue directly to them.
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <Phone className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Need Help with Cancellation or Refund?</h2>
              <p className="text-gray-600 mb-6">
                Contact our customer service team for assistance with cancellations and refunds
              </p>
              <div className="space-y-2">
                <p className="text-lg"><span className="font-semibold">Phone:</span> Available Monday - Friday (9:00 AM - 6:00 PM)</p>
                <p className="text-lg"><span className="font-semibold">Website:</span> https://www.anmolfurniturebazar.com/</p>
                <p className="text-gray-600">Use the contact information provided on our website</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RefundCancellationPolicy